import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Breadcrumb, SimpleCard } from '../../components';
import React, { useCallback, useEffect, useState } from 'react';
import axios from '../../../axios';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import toast, { Toaster } from 'react-hot-toast';
import { LoadingButton } from '@mui/lab';
import { CREATE_SERVICE, GET_TAX_LIST } from '../../api';
import { MuiColorInput } from 'mui-color-input';
import { ImageUpload } from '../../components/DropZone/ImageUpload';
import createNFTUpload from '../../../assets/createNFTUpload.png';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled('div')(({ theme }) => ({
  margin: '20px',
  [theme.breakpoints.down('sm')]: { margin: '16px' },
  '& .breadcrumb': {
    marginBottom: '16px',
    [theme.breakpoints.down('sm')]: { marginBottom: '12px' },
  },
}));

const SectionHeading = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginTop: '28px',
  marginBottom: '20px',
}));

const AccentBar = styled('span')(({ color }) => ({
  display: 'inline-block',
  width: 4,
  height: 22,
  borderRadius: 4,
  background: color || '#1976d2',
  flexShrink: 0,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
}));

const typeData = [{ key: 'Residential' }, { key: 'Commercial' }, { key: 'Custom' }];

function CreateServices() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [typeDataList, setCompanyData] = useState(typeData);
  const [taxData, setTaxData] = useState([]);
  const [taxError, setTaxError] = useState('');

  const loadTaxes = useCallback(async () => {
      setTaxError('');
      await axios
        .get(`${GET_TAX_LIST}`, {
          headers: { 'Content-Type': 'application/json' },
        })
        .then((res) => setTaxData(res?.data?.data || []))
        .catch(() => {
          setTaxData([]);
          setTaxError('Taxes could not be loaded.');
        });
      setCompanyData(typeData);
  }, []);

  useEffect(() => {
    loadTaxes();
  }, [loadTaxes]);

  const schema = Yup.object().shape({
    name: Yup.string().required('Service name is required'),
    slug: Yup.string()
      .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and single hyphens only')
      .required('Service url is required'),
    type: Yup.string().required('Type name is required!'),
    tax: Yup.string().required('Tax name is required!'),
    title: Yup.string().required('Service title is required'),
    colour: Yup.string().required('Colour is required'),
    image_1: Yup.string().required('Image is required'),
    image_2: Yup.string().required('Image is required'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      slug: '',
      title: '',
      description: '',
      status: 'Draft',
      colour: '#fff000',
      type: '',
      tax: '',
      image_1: null,
      image_2: null,
    },
    validationSchema: schema,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append('colour', values?.colour);
      formData.append('type', values?.type);
      formData.append('description', values?.description);
      formData.append('image_1', values?.image_1);
      formData.append('image_2', values?.image_2);
      formData.append('name', values?.name);
      formData.append('slug', values?.slug);
      formData.append('status', values?.status);
      formData.append('tax', values?.tax);
      formData.append('title', values?.title);
      setLoading(true);
      toast.promise(
        axios.post(`${CREATE_SERVICE}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
        {
          loading: () => `Creating Service`,
          success: (res) => {
            setLoading(false);
            setTimeout(() => {
              resetForm();
              navigate(`/dashboard/services/details/${res.data.data.slug}`, {
                state: res.data.data,
              });
            }, 1000);
            return 'Service Created';
          },
          error: (err) => {
            setLoading(false);
            return err?.message;
          },
        }
      );
    },
  });

  const { errors, touched, resetForm, setFieldValue, handleSubmit, getFieldProps, values } = formik;

  useEffect(() => {
    const slug = values.name
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFieldValue('slug', slug);
  }, [setFieldValue, values.name]);

  const fieldSx = { borderRadius: '8px' };

  return (
    <Container>
      <Box className="breadcrumb">
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1f3260', mb: 0.4 }}>
          Create Service
        </Typography>
        <Breadcrumb
          routeSegments={[
            { name: 'Services', path: '/dashboard/services' },
            { name: 'Create Service' },
          ]}
        />
      </Box>

      <Box display={'flex'} justifyContent={'end'} gap={2}>
        <StyledButton disabled startIcon={<AddIcon />} variant="contained" color="primary">
          Package
        </StyledButton>
        <StyledButton disabled startIcon={<AddIcon />} variant="contained" color="primary">
          Extra
        </StyledButton>
      </Box>

      <SimpleCard>
        {taxError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadTaxes}>Retry</Button>} sx={{ mb: 2 }}>
            {taxError}
          </Alert>
        )}
        {!taxError && !taxData.length && (
          <Alert severity="info" sx={{ mb: 2 }}>No taxes are currently available.</Alert>
        )}
        <FormikProvider value={formik}>
          <Form autoComplete="off" encType="multipart/form-data" noValidate onSubmit={handleSubmit}>
            
            {/* SECTION 1 — Service Details */}
            <SectionHeading>
              <AccentBar color="#1976d2" />
              <SettingsSuggestOutlinedIcon sx={{ color: '#1976d2', fontSize: 20 }} />
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1f3260' }}>
                Service Details
              </Typography>
            </SectionHeading>

            <Grid container spacing={2.5}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField
                  size="small" fullWidth label="Type *" select
                  {...getFieldProps('type')}
                  error={Boolean(touched.type && errors.type)}
                  helperText={touched.type && errors.type}
                  InputProps={{ sx: fieldSx }}
                >
                  {typeDataList.map((data) => (
                    <MenuItem key={data?.key} value={data?.key}>
                      {data?.key}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item lg={6} md={6} sm={12} xs={12}>
                <TextField
                  size="small" fullWidth label="Tax *" select
                  disabled={Boolean(taxError) || !taxData.length}
                  {...getFieldProps('tax')}
                  error={Boolean(touched.tax && errors.tax)}
                  helperText={touched.tax && errors.tax}
                  InputProps={{ sx: fieldSx }}
                >
                  {taxData.map((data) => (
                    <MenuItem key={data?.id} value={data?.id}>
                      {data?.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Tooltip title="A name will only appear internally to reference this booking page">
                  <TextField
                    size="small" fullWidth label="Service Name *"
                    {...getFieldProps('name')}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    InputProps={{ sx: fieldSx }}
                  />
                </Tooltip>
              </Grid>

              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Tooltip title="A consumer facing service title will appear in bookings and emails">
                  <TextField
                    size="small" fullWidth label="Service Title *"
                    {...getFieldProps('title')}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    InputProps={{ sx: fieldSx }}
                  />
                </Tooltip>
              </Grid>

              <Grid item sm={12} xs={12}>
                <TextField
                  size="small" fullWidth label="Service URL *"
                  InputProps={{
                    sx: fieldSx,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: '#8a869f', fontSize: '14px' }}>https://cleany.com/booking/</Typography>
                      </InputAdornment>
                    ),
                  }}
                  {...getFieldProps('slug')}
                  error={Boolean(touched.slug && errors.slug)}
                  helperText={touched.slug && errors.slug}
                />
              </Grid>

              <Grid item sm={12} xs={12}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', mb: 1 }}>Service Theme Color</Typography>
                <MuiColorInput
                  format="hex" size="small" fullWidth
                  value={values.colour}
                  error={Boolean(touched.colour && errors.colour)}
                  helperText={touched.colour && errors.colour}
                  onChange={(color) => setFieldValue('colour', color)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mt: 4, borderStyle: 'dashed', borderColor: '#e8eaf0' }} />

            {/* SECTION 2 — Uploads */}
            <SectionHeading>
              <AccentBar color="#1565c0" />
              <PhotoLibraryOutlinedIcon sx={{ color: '#1565c0', fontSize: 20 }} />
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1f3260' }}>
                Service Media
              </Typography>
            </SectionHeading>

            <Grid container spacing={2.5}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Box sx={{ border: '1.5px dashed #d1d5e4', borderRadius: '10px', p: 2, background: '#f8f9fc' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f3260', mb: 0.5 }}>Thumbnail Photo</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#8a869f', mb: 1.5 }}>Supported formats: JPG, PNG, SVG — Max 5 MB</Typography>
                  <ImageUpload
                    name="image_1"
                    empty={!values?.image_1}
                    src={values?.image_1 || createNFTUpload}
                    onChange={(e, file) => setFieldValue(e, file)}
                    error={touched.image_1 && Boolean(errors.image_1)}
                    helperText={touched.image_1 && errors.image_1}
                    buttonProps={{ className: '!flex !justify-start !p-0' }}
                  />
                </Box>
              </Grid>

              <Grid item lg={6} md={6} sm={12} xs={12}>
                <Box sx={{ border: '1.5px dashed #d1d5e4', borderRadius: '10px', p: 2, background: '#f8f9fc' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1f3260', mb: 0.5 }}>Banner Photo</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#8a869f', mb: 1.5 }}>Supported formats: JPG, PNG, SVG — Max 5 MB</Typography>
                  <ImageUpload
                    name="image_2"
                    empty={!values?.image_2}
                    src={values?.image_2 || createNFTUpload}
                    onChange={(e, file) => setFieldValue(e, file)}
                    error={touched.image_2 && Boolean(errors.image_2)}
                    helperText={touched.image_2 && errors.image_2}
                    buttonProps={{ className: '!flex !justify-start !p-0' }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Submit Button */}
            <Box display={'flex'} alignItems="center" justifyContent={'end'} sx={{ mb: 2, mt: 4 }}>
              <LoadingButton
                type="submit"
                color="primary"
                loading={loading}
                disabled={Boolean(taxError) || !taxData.length}
                variant="contained"
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  },
                }}
              >
                Create Service
              </LoadingButton>
            </Box>
          </Form>
        </FormikProvider>
      </SimpleCard>
      <Toaster position="top-right" />
    </Container>
  );
}

export default CreateServices;
